import React, { ChangeEvent, ReactElement, useState } from 'react';
import './upload-button.scss';
import classnames from 'classnames';
import { FormattedMessage, useIntl } from 'react-intl';
import {
    AUDIO_FILE_INPUT_ACCEPT,
    SUPPORTED_ASSETS_TYPES,
    SUPPORTED_CSV_TYPES,
    SUPPORTED_HTML_TYPES,
    SUPPORTED_IMAGE_TYPES,
    SUPPORTED_JSON_TYPES,
    SUPPORTED_REPORT_TYPES,
    SUPPORTED_ZIP_TYPES
} from '../../../../constants/file.constants';
import { FileInput } from './file-input/file-input';

export enum FILE_TYPE {
    IMG = 'img',
    CSV = 'csv',
    ZIP = 'zip',
    HTML = 'html',
    REPORT = 'report',
    JSON = 'json',
    ASSETS = 'assets',
    AUDIO = 'audio'
}

export interface ButtonProp {
    fileName?: string;
    className?: string | null;
    onChange?: (e: File | File[]) => void;
    icon?: React.ComponentClass | React.FunctionComponent;
    uploadedFileType?: FILE_TYPE;
    withSVGType?: boolean;
    disabled?: boolean;
    additionalText?: string | ReactElement;
    multiple?: boolean;
}

export interface MultipleUploadProp {
    className: string | null;
    onChange: (e: File | File[]) => void;
    acceptedFileTypes: string[];
    title: JSX.Element;
    type?: 'upload' | 'default';
}

export const MultipleUploadInput = (props: MultipleUploadProp) => {
    const { onChange, className, acceptedFileTypes, title, type = 'upload' } = props;

    const handleMultiUploadFiles = e => {
        let chosenFiles = null;
        if (e?.dataTransfer?.files && e.dataTransfer?.files?.[0]) {
            chosenFiles = Array.from(e.dataTransfer.files);
        } else {
            e.persist();
            chosenFiles = Array.from(e.target.files);
            onChange(chosenFiles);
            e.target.value = null;
        }
    };

    return (
        <FileInput
            className={className}
            inputClassName='upload-button__input'
            type={type}
            title={title}
            acceptedFileTypes={acceptedFileTypes.join()}
            onChange={handleMultiUploadFiles}
            multiple
        />
    );
};

const UploadButton = React.forwardRef((props: ButtonProp, ref: React.Ref<HTMLInputElement>): JSX.Element => {
    const {
        onChange,
        fileName,
        uploadedFileType = FILE_TYPE.CSV,
        withSVGType = true,
        disabled = false,
        additionalText,
        multiple,
        className
    } = props;
    const [isHovered, setHovered] = useState(false);
    const intl = useIntl();
    let fileTypeName: string;
    let title: JSX.Element;
    let acceptedFileTypes: string[];

    switch (uploadedFileType) {
        case FILE_TYPE.CSV:
            fileTypeName = intl.formatMessage({
                id: 'oe.uploadedFileType.csv',
                description: 'file type',
                defaultMessage: 'a CSV file'
            });
            title = <FormattedMessage
                description='Label text'
                defaultMessage='Upload CSV file'
                id='oe.uploadedFileTitle.csv'
            />;
            acceptedFileTypes = SUPPORTED_CSV_TYPES;
            break;
        case FILE_TYPE.IMG:
            fileTypeName = intl.formatMessage({
                id: 'oe.uploadedFileType.img',
                description: 'file type',
                defaultMessage: 'an image'
            });
            title = <FormattedMessage
                description='Label text'
                defaultMessage='Upload Image'
                id='oe.uploadedFileTitle.image'
            />;
            acceptedFileTypes = withSVGType ? SUPPORTED_IMAGE_TYPES : SUPPORTED_IMAGE_TYPES.slice(1);
            break;
        case FILE_TYPE.ZIP:
            fileTypeName = intl.formatMessage({
                id: 'oe.uploadedFileType.zip',
                description: 'file type',
                defaultMessage: 'a ZIP file'
            });
            title = <FormattedMessage
                description='Label text'
                defaultMessage='Upload ZIP file'
                id='oe.uploadedFileTitle.zip'
            />;
            acceptedFileTypes = SUPPORTED_ZIP_TYPES;
            break;
        case FILE_TYPE.HTML:
            fileTypeName = intl.formatMessage({
                id: 'oe.uploadedFileType.html',
                description: 'file type',
                defaultMessage: 'a HTML file'
            });
            title = <FormattedMessage
                description='Label text'
                defaultMessage='Upload HTML file'
                id='oe.uploadedFileTitle.html'
            />;
            acceptedFileTypes = SUPPORTED_HTML_TYPES;
            break;
        case FILE_TYPE.REPORT:
            fileTypeName = intl.formatMessage({
                id: 'oe.uploadedFileType.report',
                description: 'file type',
                defaultMessage: 'a Report file'
            });
            title = <FormattedMessage
                description='Label text'
                defaultMessage='Upload Report file'
                id='oe.uploadedFileTitle.report'
            />;
            acceptedFileTypes = SUPPORTED_REPORT_TYPES;
            break;
        case FILE_TYPE.JSON:
            fileTypeName = intl.formatMessage({
                id: 'oe.uploadedFileType.json',
                description: 'file type',
                defaultMessage: 'a JSON file'
            });
            title = <FormattedMessage
                description='Label text'
                defaultMessage='Upload JSON file'
                id='oe.uploadedFileTitle.json'
            />;
            acceptedFileTypes = SUPPORTED_JSON_TYPES;
            break;
        case FILE_TYPE.AUDIO:
            fileTypeName = intl.formatMessage({
                id: 'oe.uploadedFileType.audio',
                description: 'file type',
                defaultMessage: 'an audio file'
            });
            title = <FormattedMessage
                description='Label text'
                defaultMessage='Upload Audio file'
                id='oe.uploadedFileTitle.audio'
            />;
            acceptedFileTypes = AUDIO_FILE_INPUT_ACCEPT;
            break;
        case FILE_TYPE.ASSETS:
            fileTypeName = intl.formatMessage({
                id: 'oe.uploadedFileType.assets',
                description: 'file type',
                defaultMessage: 'files'
            });
            title = <FormattedMessage
                description='Label text'
                defaultMessage='Upload Files'
                id='oe.uploadedFileTitle.assets'
            />;
            acceptedFileTypes = SUPPORTED_ASSETS_TYPES;
            break;
    }

    const uploadFileHandler = (e: ChangeEvent<HTMLInputElement> | DragEvent) => {
        if (disabled) return;
        if ('dataTransfer' in e) {
            onChange(e.dataTransfer.files[0]);
        } else {
            e.persist();
            onChange(e.target.files[0]);
            e.target.value = null;
        }
    };

    const handleDragLeave = e => {
        e.preventDefault();
        e.stopPropagation();
        setHovered(false);
    };
    const handleDragOver = e => {
        e.preventDefault();
        e.stopPropagation();
        setHovered(true);
    };
    const handleDrop = e => {
        e.preventDefault();
        e.stopPropagation();
        e.nativeEvent.stopImmediatePropagation();
        setHovered(false);
        if (multiple && e.dataTransfer.files && e.dataTransfer.files[0]) {
            onChange(Array.from(e.dataTransfer.files));
        }
        else uploadFileHandler(e);
    };

    return (
        <div
            className={classnames('draggable-area', {
                'draggable-area--hovered': isHovered,
                'disabled': disabled
            })}
            onDrop={e => handleDrop(e)}
            onDragOver={e => handleDragOver(e)}
            onDragLeave={e => handleDragLeave(e)}
        >
            {multiple
                ?
                <MultipleUploadInput
                    onChange={onChange}
                    acceptedFileTypes={acceptedFileTypes}
                    className={className}
                    title={title}
                />
                :
                <FileInput
                    className={className}
                    inputClassName='upload-button__input'
                    type='upload'
                    title={title}
                    acceptedFileTypes={acceptedFileTypes.join()}
                    ref={ref}
                    onChange={uploadFileHandler}
                    disabled={disabled}
                />
            }
            <p className='draggable-area__hint'>
                {fileName ||
                    <>
                        <FormattedMessage
                            description='Label text'
                            defaultMessage='Or drag-and-drop {value}'
                            id='oe.uploadedFileType.dragAndDropFile'
                            values={{
                                value: fileTypeName
                            }}
                        />
                        {additionalText}
                    </>
                }
            </p>
        </div>
    );
});

export default UploadButton;
